import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/common/Button';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useAcademicYearPicker } from '@/hooks/usePickers';
import { enrollmentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';

/**
 * The year-end sweep: completes every enrolment still ACTIVE in a year.
 *
 * Promotion must come first. Promoted enrolments are already closed as
 * PROMOTED and untouched here; anything promoted after the sweep would be
 * promoting a student whose year has already been marked complete. Hence a
 * second, explicit confirmation — the sweep cannot be undone in bulk.
 */
export function CloseYearModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const years = useAcademicYearPicker();

  const [academicYearId, setAcademicYearId] = useState('');
  const [completionDate, setCompletionDate] = useState(new Date().toISOString().slice(0, 10));
  const [confirming, setConfirming] = useState(false);

  const yearName = years.data?.find((year) => year.id === academicYearId)?.name ?? 'this year';

  const closeYear = useMutation({
    mutationFn: () => enrollmentsApi.closeYear(academicYearId, completionDate),
    onSuccess: (result) => {
      toast.success(
        result.closed === 1 ? '1 enrolment completed' : `${result.closed} enrolments completed`,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.enrollments.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.studentProfile.all });
      onClose();
    },
    onError: (error) => {
      handleApiError(error);
      setConfirming(false);
    },
  });

  return (
    <>
      <Modal
        open={!confirming}
        title="Close academic year"
        description="Completes every enrolment still active in the chosen year."
        onClose={onClose}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={academicYearId === '' || completionDate === ''}
              onClick={() => setConfirming(true)}
            >
              Continue
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="rounded-lg bg-warning-soft px-3 py-2 text-xs text-warning">
            Run promotion first. Students already promoted are left alone; everyone else still active in
            the year is marked completed.
          </p>
          <SelectField
            label="Academic year"
            required
            value={academicYearId}
            onChange={(event) => setAcademicYearId(event.target.value)}
            hint={years.error !== null ? 'Could not load academic years.' : undefined}
          >
            <option value="">{years.isPending ? 'Loading…' : 'Select a year…'}</option>
            {years.data?.map((year) => (
              <option key={year.id} value={year.id}>
                {year.name}
                {year.current ? ' (current)' : ''}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Completion date"
            type="date"
            required
            value={completionDate}
            onChange={(event) => setCompletionDate(event.target.value)}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={confirming}
        title={`Close ${yearName}?`}
        message={`Every enrolment still ACTIVE in ${yearName} will be completed as of ${completionDate}. Promotion should already have run — anyone not yet promoted will be closed out of the year rather than moved up. This cannot be undone in bulk.`}
        confirmLabel="Close year"
        destructive
        loading={closeYear.isPending}
        onConfirm={() => closeYear.mutate()}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}
